import React from 'react';
import { Card } from '@vehix/portal/components/ui/card';
import { CalendarClock, ExternalLink } from 'lucide-react';
import { cn } from '@vehix/portal/lib/utils';
import type { ScheduleContext } from '@vehix/shared/types/recallSnapshots';

interface Props {
  context: ScheduleContext;
}

function formatRelative(date: Date | null): string {
  if (!date) return '—';
  const ms = date.getTime() - Date.now();
  if (ms < 0) return 'overdue';
  const hours = Math.round(ms / (60 * 60 * 1000));
  if (hours < 1) return '< 1h';
  if (hours < 24) return `in ${hours}h`;
  const days = Math.floor(hours / 24);
  return `in ${days}d ${hours % 24}h`;
}

const RUN_DOT: Record<ScheduleContext['recent_runs'][number]['status'], string> = {
  completed: 'bg-emerald-500',
  failed: 'bg-red-500',
  running: 'bg-amber-400',
  other: 'bg-muted-foreground/40'
};

export const ScheduleContextCard: React.FC<Props> = ({ context }) => {
  const nextLabel = context.next_fire_at
    ? `${context.next_fire_at.toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })} (${formatRelative(context.next_fire_at)})`
    : 'unknown';

  return (
    <Card className='p-4'>
      <div className='flex items-start gap-3'>
        <CalendarClock className='h-5 w-5 text-primary flex-shrink-0 mt-0.5' />
        <div className='min-w-0 flex-1 space-y-2'>
          <div className='flex items-baseline justify-between gap-2'>
            <h3 className='text-sm font-semibold'>Schedule</h3>
            <a
              href={context.workflow_management_url}
              className='text-[11px] text-primary hover:underline inline-flex items-center gap-0.5'
            >
              View in Workflow Mgmt <ExternalLink className='h-3 w-3' />
            </a>
          </div>
          <p className='text-xs text-foreground/90'>{context.human_readable}</p>
          <p className='text-[11px] text-muted-foreground'>Next: {nextLabel}</p>
          {context.recent_runs.length > 0 && (
            <div className='flex items-center gap-1.5 pt-0.5'>
              <span className='text-[11px] text-muted-foreground'>Last {context.recent_runs.length}:</span>
              {context.recent_runs.map((r, i) => (
                <span
                  key={i}
                  className={cn('h-2 w-2 rounded-full', RUN_DOT[r.status])}
                  title={`${r.status} · ${new Date(r.at).toLocaleString()}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default ScheduleContextCard;
